import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';
import { ChevronRight, User, PlusCircle, Eye, X } from 'lucide-react';
import apiService from '../../services/api';
import '../../addproduct.css';
import WhatsAppIntegration from '../WhatsAppNotification/WhatsAppIntegration';
import whatsappService from '../../services/whatsapp';
import invoiceGenerator from '../../services/invoiceGenerator';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import SubscriptionExpiredModal from '../Modals/SubscriptionExpiredModal';
import AddProduct from '../../AddProduct';
import AddSupplier from './AddSupplier';
import dateUtils from '../../utils/dateUtils';


const initialFormData = {
  customer: '',
  customerId: '',
  billingAddress: '',
  shippingAddress: '',
  shippingCity: '',
  shippingState: '',
  shippingCountry: '',
  shippingZipCode: '',
  payTerm: '',
  payTermPeriod: 'Months',
  commissionAgent: '',
  saleDate: dateUtils.getChicagoISOString().split('T')[0],
  status: 'Final',
  invoiceScheme: '',
  invoiceNo: '',
  locationId: '',
  products: [],
  productName: '',
  quantity: '',
  unitPrice: '',
  discount: '',
  tax: '',
  discountType: 'Percentage',
  discountAmount: '0.00',
  redeemedAmount: '0.00',
  orderTax: 'None',
  orderTaxAmount: '0.00',
  shippingCharges: '0.00',
  roundOff: '0',
  totalPayable: '0.00',
  productAmount: '0.00',
  payments: [],
  amount: '0.00',
  paidOn: dateUtils.nowUTC(),
  paymentMethod: 'Cash',
  paymentAccount: 'None',
  paymentNote: '',
  sellNote: '',
  additionalNotes: '',
  staffNote: '',
  shippingDetails: '',
  deliveredTo: '',
  deliveryPerson: '',
  shippingStatus: '',
  serviceStaff: '',
  checkNo: '',
  checkExpiryDate: '',
  bankSubmissionDate: '',
  checkImage: null,
  bankAccountNumber: '',
  cardNumber: '',
  cardHolderName: '',
  cardType: 'visa',
  cardMonth: '',
  cardYear: '',
  cardSecurity: '',
  cardTransactionNumber: '',
  cardExpiry: '',
  cardCvv: '',
  serviceCustomField1: '',
  serviceCustomField2: '',
  serviceCustomField3: '',
  serviceCustomField4: '',
  serviceCustomField5: '',
  serviceCustomField6: '',
  additionalExpenseKey1: '',
  additionalExpenseValue1: '',
  additionalExpenseKey2: '',
  additionalExpenseValue2: '',
  additionalExpenseKey3: '',
  additionalExpenseValue3: '',
  additionalExpenseKey4: '',
  additionalExpenseValue4: '',
  additionalExpenses: [
    { id: 1, name: '', amount: '' },
    { id: 2, name: '', amount: '' },
    { id: 3, name: '', amount: '' },
    { id: 4, name: '', amount: '' }
  ]
};

const AddSell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, enabledModules } = usePermissionContext();
  const { salesCommissionAgent, isPayTermRequired, isCommissionAgentRequired } = useBusinessSettings();
  const { isQuotaAvailable } = useSubscriptionContext();
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const editSale = location.state?.editSale || null;
  const isEditMode = !!editSale;
  const isSalesOrder = location.state?.sale_type === 'sales_order' || location.state?.salesOrder || new URLSearchParams(window.location.search).get('sale_type') === 'sales_order';
  // Customer states
  const [customers, setCustomers] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerError, setCustomerError] = useState('');

  // Tax states
  const [taxRates, setTaxRates] = useState([
    { id: 0, name: 'None', amount: 0, type: 'percentage' },
    { id: 1, name: 'Beer Tax', amount: 0, type: 'percentage' },
    { id: 2, name: 'Food', amount: 0, type: 'percentage' },
    { id: 3, name: 'Wine And Liquor 10.5%', amount: 10.5, type: 'percentage' },
    { id: 4, name: 'No Tax', amount: 0, type: 'percentage' },
    { id: 5, name: 'Sales Tax', amount: 0, type: 'percentage' },
    { id: 6, name: 'Cigarettes', amount: 0, type: 'percentage' }
  ]);
  const [isLoadingTaxRates, setIsLoadingTaxRates] = useState(false);
  const [taxError, setTaxError] = useState('');

  // Payment methods states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');

  // Sales Commission Agents
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Service Staff
  const [serviceStaffList, setServiceStaffList] = useState([]);
  const [isLoadingServiceStaff, setIsLoadingServiceStaff] = useState(false);
  const [usersList, setUsersList] = useState([]);

  const [formData, setFormData] = useState({
    ...initialFormData,
    saleDate: dateUtils.getChicagoISOString().split('T')[0]
  });

  // File input ref for check image
  const checkImageInputRef = React.useRef(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Customer dropdown states
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // Product search states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productError, setProductError] = useState('');
  const [displayLimit, setDisplayLimit] = useState(50);

  // Modal states
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);

  const [advanceBalance, setAdvanceBalance] = useState('0.00');

  // Derived state for balance and change return
  const totalPaid = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalPayableVal = parseFloat(formData.totalPayable) || 0;
  let balanceVal = totalPayableVal - totalPaid;
  let changeReturnVal = 0;

  if (balanceVal < 0) {
    changeReturnVal = Math.abs(balanceVal);
    balanceVal = 0;
  }

  const balance = balanceVal.toFixed(2);
  const changeReturn = changeReturnVal.toFixed(2);

  // State for additional expenses visibility
  const [showAdditionalExpenses, setShowAdditionalExpenses] = useState(false);

  // State for calculation breakdown expansion
  const [isCalculationExpanded, setIsCalculationExpanded] = useState(false);
  const [isExpensesExpanded, setIsExpensesExpanded] = useState(false);

  // Payment accounts state
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [isLoadingPaymentAccounts, setIsLoadingPaymentAccounts] = useState(false);
  const [paymentAccountsError, setPaymentAccountsError] = useState('');

  // WhatsApp notification state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [lastSaleData, setLastSaleData] = useState(null);

  // Business locations state
  const [businessLocations, setBusinessLocations] = useState([{ id: 1, name: 'Lefty Liquor' }]);

  // Invoice Schemes state
  const [invoiceSchemes, setInvoiceSchemes] = useState([]);
  const [isLoadingInvoiceSchemes, setIsLoadingInvoiceSchemes] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.customer || !formData.customer.trim()) {
      newErrors.customer = 'Customer is required';
    }

    if (!formData.locationId) {
      newErrors.locationId = 'Business location is required';
    }

    if (!formData.saleDate || !formData.saleDate.trim()) {
      newErrors.saleDate = 'Sale date is required';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    if (isPayTermRequired && (!formData.payTerm || !formData.payTerm.toString().trim())) {
      newErrors.payTerm = 'Pay term is required';
    }

    if (isCommissionAgentRequired && salesCommissionAgent && salesCommissionAgent !== 'disable' && salesCommissionAgent !== 'logged_in_user' && (!formData.commissionAgent || !formData.commissionAgent.toString().trim())) {
      newErrors.commissionAgent = 'Commission agent is required';
    }

    // Validate total payable
    if (parseFloat(formData.totalPayable) <= 0) {
      newErrors.totalPayable = 'Total payable must be greater than 0';
    }

    setErrors(newErrors);

    const errorValues = Object.values(newErrors);
    if (errorValues.length > 0) {
      toast.error(errorValues[0]);
    }

    return errorValues.length === 0;
  };

  // Load business locations silently — needed to get the correct location_id for saving
  const loadBusinessLocation = async () => {
    try {
      const response = await apiService.getBusinessLocations();
      const locations = response.data || (Array.isArray(response) ? response : []);

      if (locations.length > 0) {
        setBusinessLocations(locations);

        // Auto-select logged-in user's location if not in edit mode
        if (!isEditMode && !formData.locationId) {
          try {
            const userDetails = await apiService.getUserDetails();
            const userLocationId = userDetails.data?.location_id || userDetails.location_id;

            if (userLocationId) {
              const matchedLocation = locations.find(loc => String(loc.id) === String(userLocationId));
              if (matchedLocation) {
      /* console.log('[AddSell] Auto-selecting user location:', userLocationId); */
                setFormData(prev => ({ ...prev, locationId: String(userLocationId) }));
              } else {
      /* console.log('[AddSell] User location ID', userLocationId, 'not found in locations list, defaulting to first.'); */
                setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
              }
            } else {
              setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
            }
          } catch (userErr) {
      /* console.error('[AddSell] Error fetching user details for location:', userErr); */
            setFormData(prev => ({ ...prev, locationId: String(locations[0].id) }));
          }
        }
      /* console.log('[AddSell] Locations loaded, total:', locations.length); */
      }
    } catch (error) {
      /* console.error('[AddSell] Could not load location, using default ID=1:', error); */
    }
  };

  // Load products, customers, tax rates, and locations on component mount
  useEffect(() => {
    if (apiService.isTokenValid()) {
      loadProducts();
      loadCustomers();
      loadTaxRates();
      loadBusinessLocation();
      loadInvoiceSchemes();
      loadUsers();
    } else {
      /* console.log('User not authenticated, skipping data loading'); */
    }
  }, []);

  useEffect(() => {
    if (apiService.isTokenValid() && salesCommissionAgent) {
      loadAgents();
    }
  }, [salesCommissionAgent]);

  useEffect(() => {
    if (apiService.isTokenValid() && (enabledModules.includes('service_staff') || enabledModules.includes('tables'))) {
      loadServiceStaff();
    }
  }, [enabledModules]);

  useEffect(() => {
    if (apiService.isTokenValid()) {
      if (formData.locationId) {
        loadPaymentAccounts(formData.locationId);
        loadPaymentMethods(formData.locationId);
      } else {
        setPaymentAccounts([]);
        setPaymentMethods([]);
      }
    }
  }, [formData.locationId]);

  // Pre-fill form when editing an existing sale
  useEffect(() => {
    const fetchFullSaleDetails = async () => {
      if (editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
      /* console.log('Edit mode - fetching full sale details for ID:', saleId); */
        try {
          const response = await apiService.getSale(saleId);
          const apiData = response?.data?.transaction || response?.data?.[0] || response?.data || response;
      /* console.log('Fetched full sale details:', apiData); */

          // Build products array from sell_lines using the fetched full apiData
          const editProducts = (apiData.sell_lines || []).map(line => {
      /* console.log('=== LOADING SELL LINE FOR EDIT ==='); */
      /* console.log('Raw sell_line data:', line); */
      /* console.log('product_id:', line.product_id); */
      /* console.log('variation_id:', line.variation_id); */

            const productData = {
              id: line.product_id || line.id,
              variation_id: line.variation_id,
              name: line.product?.name || line.variations?.name || line.product_name || line.name || `Product #${line.product_id}`,
              sku: line.product?.sku || line.variations?.sub_sku || line.sku || '',
              quantity: parseFloat(line.quantity) || 1,
              unitPrice: parseFloat(line.unit_price || line.unitPrice || 0),
              purchasePrice: parseFloat(line.purchase_price || 0),
              discount: parseFloat(line.line_discount_amount || line.discount || 0),
              discountType: line.line_discount_type ? (line.line_discount_type === 'percentage' ? 'Percentage' : 'Fixed') : 'Percentage',
              tax: line.tax?.name || line.tax_name || line.tax || 'None',
              priceIncTax: parseFloat(line.unit_price_inc_tax || line.priceIncTax || line.unit_price || 0),
              subtotal: parseFloat(line.unit_price_inc_tax || line.priceIncTax || line.unit_price || 0) * (parseFloat(line.quantity) || 1)
            };
      /* console.log('Mapped product data:', productData); */
      /* console.log('=== END LOADING SELL LINE ==='); */

            return productData;
          });

          const productAmount = editProducts.reduce((sum, p) => sum + (p.subtotal || 0), 0);

          setFormData(prev => ({
            ...prev,
            customer: apiData.contact?.name || editSale.customer || '',
            customerId: apiData.contact_id || editSale.customerId || '',
            billingAddress: apiData.contact?.address_line_1 || editSale.billingAddress || '',
            shippingAddress: apiData.shipping_address || editSale.shippingAddress || '',
            saleDate: dateUtils.getChicagoISOString().split('T')[0],
            status: apiData.sub_status === 'quotation' ? 'Quotation' :
              apiData.sub_status === 'proforma' ? 'Proforma' :
                apiData.status === 'draft' ? 'Draft' :
                  apiData.status === 'final' ? 'Final' : 'Final',
            invoiceNo: apiData.invoice_no || editSale.invoiceNo || '',
            invoiceScheme: apiData.invoice_scheme_id || editSale.invoiceScheme || '',
            locationId: apiData.location_id || editSale.locationId || '1',
            commissionAgent: apiData.commission_agent || editSale.commissionAgent || '',
            serviceStaff: apiData.res_waiter_id || editSale.serviceStaff || '',
            payTerm: apiData.pay_term_number || editSale.payTerm || '',
            payTermPeriod: apiData.pay_term_type ? (apiData.pay_term_type.charAt(0).toUpperCase() + apiData.pay_term_type.slice(1).toLowerCase()) : (editSale.payTermPeriod || 'Months'),
            products: editProducts,
            productAmount: productAmount.toFixed(2),
            discountType: apiData.discount_type === 'percentage' ? 'Percentage' : (apiData.discount_type || 'Percentage'),
            discountAmount: apiData.discount_amount || editSale.discountAmount || '0.00',
            orderTax: apiData.tax?.name || editSale.orderTax || 'None',
            orderTaxAmount: apiData.tax_amount || editSale.orderTaxAmount || '0.00',
            shippingCharges: apiData.shipping_charges || editSale.shippingCharges || '0.00',
            shippingDetails: apiData.shipping_details || editSale.shippingDetails || '',
            shippingStatus: apiData.shipping_status || editSale.shippingStatus || '',
            deliveredTo: apiData.delivered_to || editSale.deliveredTo || '',
            deliveryPerson: apiData.delivery_person || editSale.deliveryPerson || '',
            sellNote: apiData.additional_notes || editSale.sellNote || '',
            staffNote: apiData.staff_note || editSale.staffNote || '',
            roundOff: apiData.round_off_amount || editSale.roundOff || '0',
            totalPayable: apiData.final_total || editSale.totalPayable || '0.00',

            // Service Custom Fields
            serviceCustomField1: apiData.service_custom_field_1 || '',
            serviceCustomField2: apiData.service_custom_field_2 || '',
            serviceCustomField3: apiData.service_custom_field_3 || '',
            serviceCustomField4: apiData.service_custom_field_4 || '',
            serviceCustomField5: apiData.service_custom_field_5 || '',
            serviceCustomField6: apiData.service_custom_field_6 || '',

            // Additional Expenses
            additionalExpenseKey1: apiData.additional_expense_key_1 || '',
            additionalExpenseValue1: apiData.additional_expense_value_1 || '',
            additionalExpenseKey2: apiData.additional_expense_key_2 || '',
            additionalExpenseValue2: apiData.additional_expense_value_2 || '',
            additionalExpenseKey3: apiData.additional_expense_key_3 || '',
            additionalExpenseValue3: apiData.additional_expense_value_3 || '',
            additionalExpenseKey4: apiData.additional_expense_key_4 || '',
            additionalExpenseValue4: apiData.additional_expense_value_4 || '',

            // Payment Lines - map from API payment_lines
            payments: (apiData.payment_lines || []).map(payment => {
              const methodStr = payment.method || payment.payment_method || 'cash';
              return {
                id: payment.id,
                amount: parseFloat(payment.amount || 0).toFixed(2),
                paidOn: payment.paid_on ? dateUtils.getChicagoISOString(payment.paid_on) : dateUtils.getChicagoISOString(),
                paymentMethod: methodStr.charAt(0).toUpperCase() + methodStr.slice(1),
                paymentAccount: payment.account_id || 'None',
                account_id: payment.account_id,
                paymentNote: payment.note || '',
                cardNumber: payment.card_number || '',
                cardHolderName: payment.card_holder_name || '',
                cardExpiry: payment.card_month && payment.card_year ? `${payment.card_month}/${payment.card_year}` : '',
                cardCvv: payment.card_security || '',
                checkNumber: payment.cheque_number || '',
                checkExpiryDate: payment.cheque_expiry_date || '',
                bankSubmissionDate: payment.bank_submission_date || ''
              };
            })
          }));

          setCustomerSearchTerm(apiData.contact?.name || editSale.customer || '');
        } catch (err) {
      /* console.error('Error fetching full sale details during edit:', err); */
        }
      }
    };

    fetchFullSaleDetails();
  }, [editSale]);


  // Recalculate totals when products or pricing changes
  useEffect(() => {
    calculateTotals(formData);
  }, [formData.productAmount, formData.discountAmount, formData.orderTaxAmount, formData.shippingCharges, formData.roundOff, formData.additionalExpenses, formData.products]);

  // Recalculate product subtotal when products change
  useEffect(() => {
    calculateOrderTotals();
  }, [formData.products]);

  // Auto-update amount field when total payable or payments change
  useEffect(() => {
    const paid = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const total = parseFloat(formData.totalPayable) || 0;
    const remaining = Math.max(0, total - paid);

    setFormData(prev => {
      return {
        ...prev,
        amount: remaining.toFixed(2)
      };
    });
  }, [formData.totalPayable, formData.payments]);

  const loadPaymentAccounts = async (locId = null) => {
    const targetLoc = locId || formData.locationId || null;
    if (!targetLoc) {
      setPaymentAccounts([]);
      return;
    }
    try {
      setIsLoadingPaymentAccounts(true);
      setPaymentAccountsError('');
      const params = { location_id: targetLoc, per_page: -1 };
      const accountsData = await apiService.getPaymentAccounts(params);
      const accounts = accountsData.data || accountsData || [];
      setPaymentAccounts(accounts);
    } catch (error) {
      setPaymentAccountsError('Failed to load payment accounts');
      /* console.error('Error loading payment accounts:', error); */
      setPaymentAccounts([]);
    } finally {
      setIsLoadingPaymentAccounts(false);
    }
  };



  const loadAgents = async () => {
    try {
      setIsLoadingAgents(true);
      let data = [];
      if (salesCommissionAgent === 'cmsn_agnt') {
        const res = await apiService.getSalesCommissionAgents();
        data = res.data || res || [];
      } else if (salesCommissionAgent === 'user') {
        const res = await apiService.getUsers({ per_page: -1 });
        data = res.data || res || [];
      }
      const formatted = data.map(agent => ({
        id: agent.id,
        name: agent.full_name || agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim()
      }));
      setAgents(formatted);
    } catch (error) {
      /* console.error('Error loading agents:', error); */
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const loadServiceStaff = async () => {
    try {
      setIsLoadingServiceStaff(true);
      const res = await apiService.getServiceStaff();
      const data = res.data || res || [];
      const formatted = data.map(staff => ({
        id: staff.id,
        name: staff.full_name || staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim()
      }));
      setServiceStaffList(formatted);
    } catch (error) {
      /* console.error('Error loading service staff:', error); */
    } finally {
      setIsLoadingServiceStaff(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await apiService.getUsers({ per_page: -1 });
      const data = res.data || res || [];
      const formatted = data.map(user => ({
        id: user.id,
        name: user.full_name || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User #${user.id}`
      }));
      setUsersList(formatted);
    } catch (error) {
      /* console.error('Error loading users:', error); */
    }
  };

  const loadCustomers = async () => {
    try {
      setIsLoadingCustomers(true);
      setCustomerError('');
      const customersData = await apiService.getContacts({ type: 'customer' });

      const transformedCustomers = (customersData.data || customersData || []).map(contact => ({
        id: contact.id,
        name: contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown',
        phone: contact.mobile || contact.contact_no || contact.phone || '',
        email: contact.email || '',
        address: contact.address || contact.billing_address || contact.shipping_address || '',
        balance: contact.balance,
        advance_balance: contact.advance_balance,
        due_amount: contact.due_amount,
        pay_term_number: contact.pay_term_number,
        pay_term_type: contact.pay_term_type
      }));

      if (!transformedCustomers.find(c => c.name === 'Walk-In Customer')) {
        transformedCustomers.unshift({ id: 0, name: 'Walk-In Customer', phone: '', email: '', address: '' });
      }

      setCustomers(transformedCustomers);
      setFilteredCustomers(transformedCustomers);
    } catch (error) {
      setCustomerError('Failed to load customers');
      /* console.error('Error loading customers:', error); */
      const fallbackCustomers = [{ id: 0, name: 'Walk-In Customer', phone: '', email: '', address: '' }];
      setCustomers(fallbackCustomers);
      setFilteredCustomers(fallbackCustomers);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const loadTaxRates = async () => {
    try {
      setIsLoadingTaxRates(true);
      setTaxError('');
      const taxData = await apiService.getTaxRates();

      const transformedTaxRates = (taxData.data || taxData || []).map(tax => ({
        id: tax.id,
        name: tax.name || tax.description || tax.tax_name || `Tax ${tax.id}`,
        amount: tax.amount || tax.rate || tax.percentage || 0,
        type: tax.type || 'percentage'
      }));

      if (!transformedTaxRates.find(t => t.name === 'None')) {
        transformedTaxRates.unshift({ id: 0, name: 'None', amount: 0, type: 'percentage' });
      }

      setTaxRates(transformedTaxRates);
    } catch (error) {
      setTaxError('Failed to load tax rates');
      /* console.error('Error loading tax rates:', error); */
    } finally {
      setIsLoadingTaxRates(false);
    }
  };

  const loadPaymentMethods = async (locId = null) => {
    const targetLoc = locId || formData.locationId || null;
    if (!targetLoc) {
      setPaymentMethods([]);
      return;
    }
    try {
      setIsLoadingPaymentMethods(true);
      setPaymentMethodsError('');
      const paymentMethodsData = await apiService.getPaymentMethods(targetLoc);
      let transformedPaymentMethods = [];

      if (typeof paymentMethodsData === 'object' && paymentMethodsData !== null && !Array.isArray(paymentMethodsData)) {
        transformedPaymentMethods = Object.entries(paymentMethodsData).map(([key, value]) => {
          let nameStr = typeof value === 'string' ? value : (value.name || key);
          if (nameStr.toLowerCase() === 'cheque') nameStr = 'Check';
          return {
            id: key,
            name: nameStr,
            type: key
          };
        });
      } else if (Array.isArray(paymentMethodsData?.data)) {
        transformedPaymentMethods = paymentMethodsData.data.map(method => {
          let nameStr = method.name || method.method_name || method.label || String(method.id);
          if (nameStr.toLowerCase() === 'cheque') nameStr = 'Check';
          return {
            id: method.id || method.key || method.name,
            name: nameStr,
            type: method.type || 'default'
          };
        });
      } else if (Array.isArray(paymentMethodsData)) {
        transformedPaymentMethods = paymentMethodsData.map(method => {
          let nameStr = method.name || method.method_name || method.label || String(method.id);
          if (nameStr.toLowerCase() === 'cheque') nameStr = 'Check';
          return {
            id: method.id || method.key || method.name,
            name: nameStr,
            type: method.type || 'default'
          };
        });
      }

      if (transformedPaymentMethods.length === 0) {
        transformedPaymentMethods = [
          { id: 'Cash', name: 'Cash' },
          { id: 'Card', name: 'Card' },
          { id: 'Bank Transfer', name: 'Bank Transfer' },
          { id: 'Cheque', name: 'Check' }
        ];
      }

      setPaymentMethods(transformedPaymentMethods);
    } catch (error) {
      setPaymentMethodsError('Failed to load payment methods');
      /* console.error('Error loading payment methods:', error); */
      setPaymentMethods([]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const loadInvoiceSchemes = async () => {
    try {
      setIsLoadingInvoiceSchemes(true);
      const schemesData = await apiService.getInvoiceSchemes();
      const schemes = schemesData.data?.invoice_schemes?.details || [];
      const defaultScheme = schemesData.data?.invoice_schemes?.default;

      setInvoiceSchemes(schemes);

      if (!isEditMode && !formData.invoiceScheme && schemes.length > 0) {
        if (defaultScheme) {
          setFormData(prev => ({ ...prev, invoiceScheme: defaultScheme.id }));
        } else {
          setFormData(prev => ({ ...prev, invoiceScheme: schemes[0].id }));
        }
      }
    } catch (error) {
      /* console.error('Error loading invoice schemes:', error); */
    } finally {
      setIsLoadingInvoiceSchemes(false);
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      setProductError('');
      const productsData = await apiService.getProducts({ per_page: 250 });
      setProducts(productsData.data || productsData || []);
      setFilteredProducts(productsData.data || productsData || []);
    } catch (error) {
      setProductError('Failed to load products');
      /* console.error('Error loading products:', error); */
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductSearch = async (searchTerm) => {
    setProductSearchTerm(searchTerm);
    setDisplayLimit(250);

    try {
      setIsLoadingProducts(true);
      setProductError('');

      const productsData = await apiService.getProducts({ search: searchTerm, per_page: 250 });
      let productList = productsData.data || productsData || [];

      if (searchTerm.trim() && productList.length > 0) {
        const searchLower = searchTerm.toLowerCase().trim();
        productList = productList.filter(product => {
          const name = (product.product || product.name || product.product_name || product.title || product.description || '').toLowerCase();
          const sku = (product.sku || product.code || '').toLowerCase();
          const barcode = (product.barcode || '').toLowerCase();

          const matches = name.includes(searchLower) ||
            sku.includes(searchLower) ||
            barcode.includes(searchLower);

          return matches;
        });

        productList.sort((a, b) => {
          const aSku = (a.sku || a.code || '').toLowerCase();
          const bSku = (b.sku || b.code || '').toLowerCase();
          const aBarcode = (a.barcode || '').toLowerCase();
          const bBarcode = (b.barcode || '').toLowerCase();
          const aName = (a.product || a.name || a.product_name || a.title || a.description || '').toLowerCase();
          const bName = (b.product || b.name || b.product_name || b.title || b.description || '').toLowerCase();

          const aExact = aSku === searchLower || aBarcode === searchLower;
          const bExact = bSku === searchLower || bBarcode === searchLower;

          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;

          const aStarts = aName.startsWith(searchLower);
          const bStarts = bName.startsWith(searchLower);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;

          return aName.localeCompare(bName);
        });
      }

      setProducts(productList);
      setFilteredProducts(productList);
      setShowProductDropdown(true);
    } catch (error) {
      setProductError('Failed to search products');
      /* console.error('Error searching products:', error); */
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const calculateProductTax = (unitPrice, taxName) => {
    const selectedTax = taxRates.find(tax => tax.name === taxName);
    if (selectedTax && selectedTax.amount > 0) {
      if (selectedTax.type === 'percentage') {
        return (unitPrice * selectedTax.amount) / 100;
      } else if (selectedTax.type === 'fixed') {
        return selectedTax.amount;
      }
    }
    return 0;
  };

  const handleProductSelect = (product) => {
    const unitPrice = parseFloat(
      product.unit_price ||
      product.price ||
      product.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
      0
    );

    const productName = product.product || product.name || product.product_name || product.title || product.description || 'Unknown Product';
    const productTaxName = product.tax || 'None';

    const productTaxAmount = calculateProductTax(unitPrice, productTaxName);
    const priceIncTax = unitPrice + productTaxAmount;

    const purchasePrice = parseFloat(
      product.purchase_price ||
      product.default_purchase_price ||
      product.cost ||
      product.product_variations?.[0]?.variations?.[0]?.default_purchase_price ||
      0
    );

    const stockAvailable = parseFloat(
      product.qty_available ||
      product.current_stock ||
      product.stock ||
      product.total_qty_available ||
      (product.product_variations?.[0]?.variations?.[0]?.variation_location_details?.reduce((sum, vld) => sum + (parseFloat(vld.qty_available) || 0), 0)) ||
      (product.variation_location_details?.reduce((sum, vld) => sum + (parseFloat(vld.qty_available) || 0), 0)) ||
      0
    );

    const newProduct = {
      id: product.id || Date.now(),
      variation_id: product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id,
      name: productName,
      sku: product.sku || product.code || '',
      quantity: 1,
      unitPrice: unitPrice,
      purchasePrice: purchasePrice,
      discount: 0,
      discountType: 'percentage',
      tax: productTaxName,
      priceIncTax: priceIncTax,
      subtotal: priceIncTax,
      stock_available: stockAvailable
    };

    setFormData(prev => {
      const updatedProducts = [...prev.products, newProduct];
      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productName: '',
        productAmount: subtotal.toFixed(2)
      };
    });

    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const handleProductInputFocus = () => {
    if (products.length > 0) {
      setFilteredProducts(products);
      setShowProductDropdown(true);
    } else {
      loadProducts();
    }
  };

  const handleProductInputBlur = () => {
    setTimeout(() => {
      setShowProductDropdown(false);
    }, 150);
  };

  const removeProduct = (productId) => {
    setFormData(prev => {
      const updatedProducts = prev.products.filter(product => product.id !== productId);
      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const calculateOrderTotals = () => {
    const subtotal = formData.products.reduce((sum, product) =>
      sum + (parseFloat(product.subtotal) || 0), 0
    );

    setFormData(prev => {
      const updated = {
        ...prev,
        productAmount: subtotal.toFixed(2)
      };
      calculateTotals(updated);
      return updated;
    });
  };

  const updateProductQuantity = (productId, newQuantity) => {
    const quantity = parseInt(newQuantity) || 1;
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          let discountAmount = parseFloat(product.discount) || 0;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = quantity * priceIncTax - discountAmount;
          return {
            ...product,
            quantity,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: subtotal.toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductTax = (productId, newTaxName) => {
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, newTaxName);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          let discountAmount = parseFloat(product.discount) || 0;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            tax: newTaxName,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductDiscount = (productId, newDiscount) => {
    const discount = parseFloat(newDiscount) || 0;
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          let discountAmount = discount;
          if (product.discountType === 'Percentage') {
            discountAmount = (priceIncTax * discount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            discount,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const updateProductDiscountType = (productId, newDiscountType) => {
    setFormData(prev => {
      const updatedProducts = prev.products.map(product => {
        if (product.id === productId) {
          const productTaxAmount = calculateProductTax(parseFloat(product.unitPrice) || 0, product.tax);
          const priceIncTax = (parseFloat(product.unitPrice) || 0) + productTaxAmount;

          let discountAmount = parseFloat(product.discount) || 0;
          if (newDiscountType === 'Percentage') {
            discountAmount = (priceIncTax * discountAmount) / 100;
          }

          const subtotal = (product.quantity * priceIncTax) - discountAmount;
          return {
            ...product,
            discountType: newDiscountType,
            priceIncTax: priceIncTax.toFixed(2),
            subtotal: Math.max(0, subtotal).toFixed(2)
          };
        }
        return product;
      });

      const subtotal = updatedProducts.reduce((sum, product) =>
        sum + (parseFloat(product.subtotal) || 0), 0
      );

      return {
        ...prev,
        products: updatedProducts,
        productAmount: subtotal.toFixed(2)
      };
    });

    setTimeout(() => {
      calculateTotals(formData);
    }, 50);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'orderTax') {
      handleTaxSelection(value);
    }

    if (['productAmount', 'discountAmount', 'orderTaxAmount', 'shippingCharges', 'roundOff'].includes(name)) {
      calculateTotals({ ...formData, [name]: value });
    }
  };

  const handleTaxSelection = (selectedTaxName) => {
    const selectedTax = taxRates.find(tax => tax.name === selectedTaxName);
    if (selectedTax) {
      const productAmount = parseFloat(formData.productAmount) || 0;
      const discountAmount = parseFloat(formData.discountAmount) || 0;
      const subtotal = productAmount - discountAmount;

      let taxAmount = 0;
      if (selectedTax.type === 'percentage') {
        taxAmount = (subtotal * selectedTax.amount) / 100;
      } else if (selectedTax.type === 'fixed') {
        taxAmount = selectedTax.amount;
      }

      setFormData(prev => ({
        ...prev,
        orderTaxAmount: taxAmount.toFixed(2)
      }));

      calculateTotals({ ...formData, orderTax: selectedTaxName, orderTaxAmount: taxAmount.toFixed(2) });
    } else {
      setFormData(prev => ({
        ...prev,
        orderTaxAmount: '0.00'
      }));

      calculateTotals({ ...formData, orderTax: selectedTaxName, orderTaxAmount: '0.00' });
    }
  };

  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    setShowCustomerDropdown(true);

    if (searchTerm.trim() === '') {
      setFilteredCustomers(customers);
      return;
    }

    if (searchTerm.trim().length >= 2) {
      try {
        setIsLoadingCustomers(true);
        const customersData = await apiService.getContacts({ type: 'customer', name: searchTerm });

        const transformedCustomers = (customersData.data || customersData || []).map(contact => ({
          id: contact.id,
          name: contact.name || contact.supplier_business_name || 'Unknown',
          phone: contact.mobile || '',
          email: contact.email || '',
          address: contact.address_line_1 || '',
          balance: contact.balance,
          advance_balance: contact.advance_balance,
          due_amount: contact.due_amount,
          pay_term_number: contact.pay_term_number,
          pay_term_type: contact.pay_term_type
        }));

        setFilteredCustomers(transformedCustomers);
      } catch (error) {
      /* console.error('Error searching customers:', error); */
        const filtered = customers.filter(customer =>
          customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.phone.includes(searchTerm) ||
          customer.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCustomers(filtered);
      } finally {
        setIsLoadingCustomers(false);
      }
    } else {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.name,
      customerId: customer.id,
      billingAddress: customer.address,
      shippingAddress: customer.address,
      payTerm: customer.pay_term_number || prev.payTerm || '',
      payTermPeriod: customer.pay_term_type ? (customer.pay_term_type.charAt(0).toUpperCase() + customer.pay_term_type.slice(1).toLowerCase()) : (prev.payTermPeriod || 'Months')
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);

    const customerBalance = parseFloat(customer.balance) || parseFloat(customer.advance_balance) || parseFloat(customer.due_amount) || 0;
    setAdvanceBalance(customerBalance.toString());

    if (errors.customer) {
      setErrors(prev => ({ ...prev, customer: '' }));
    }
  };

  const handleCustomerInputFocus = () => {
    setCustomerSearchTerm(formData.customer);
    setFilteredCustomers(customers);
    setShowCustomerDropdown(true);
  };

  const handleCustomerInputBlur = () => {
    setTimeout(() => {
      setShowCustomerDropdown(false);
    }, 200);
  };

  const handleAddAdditionalExpenses = () => {
    setShowAdditionalExpenses(true);
  };

  const handleExpenseChange = (index, field, value) => {
    const newExpenses = [...formData.additionalExpenses];
    newExpenses[index][field] = value;
    const updatedFormData = {
      ...formData,
      additionalExpenses: newExpenses
    };
    setFormData(updatedFormData);

    if (field === 'amount') {
      calculateTotals(updatedFormData);
    }
  };

  const handleAddPaymentEntry = () => {
    let accountName = 'None';
    if (formData.paymentAccount) {
      const selectedAccount = paymentAccounts.find(acc => acc.id == formData.paymentAccount || acc.name == formData.paymentAccount);
      if (selectedAccount) {
        accountName = selectedAccount.name || selectedAccount.account_name || selectedAccount.display_name;
      }
    }

    const newPayment = {
      id: Date.now(),
      amount: formData.amount,
      paidOn: formData.paidOn ? String(formData.paidOn).replace('T', ' ') : dateUtils.nowUTC(),
      paymentMethod: formData.paymentMethod,
      paymentAccount: accountName,
      account_id: formData.paymentAccount,
      paymentNote: formData.paymentNote,
      cardNumber: formData.cardNumber,
      cardHolderName: formData.cardHolderName,
      cardType: formData.cardType,
      cardMonth: formData.cardMonth,
      cardYear: formData.cardYear,
      cardSecurity: formData.cardSecurity,
      cardTransactionNumber: formData.cardTransactionNumber,
      checkNo: formData.checkNo,
      checkExpiryDate: formData.checkExpiryDate,
      bankSubmissionDate: formData.bankSubmissionDate,
      bankAccountNumber: formData.bankAccountNumber
    };

    setFormData(prev => {
      const updatedPayments = [...prev.payments, newPayment];
      return {
        ...prev,
        payments: updatedPayments,
        paidOn: dateUtils.nowUTC(),
        paymentMethod: 'Cash',
        paymentAccount: '',
        paymentNote: ''
      };
    });
  };

  const handleRemovePayment = (paymentId) => {
    setFormData(prev => ({
      ...prev,
      payments: prev.payments.filter(payment => payment.id !== paymentId)
    }));
  };

  const calculateTotals = (data) => {
    const productAmount = parseFloat(data.productAmount) || 0;
    let discountAmount = parseFloat(data.discountAmount) || 0;

    if (data.discountType === 'Percentage') {
      discountAmount = (productAmount * discountAmount) / 100;
    }

    const orderTaxAmount = parseFloat(data.orderTaxAmount) || 0;
    const shippingCharges = parseFloat(data.shippingCharges) || 0;
    const roundOff = parseFloat(data.roundOff) || 0;

    const additionalExpensesTotal = data.additionalExpenses ?
      data.additionalExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0) : 0;

    const subtotal = productAmount - discountAmount;
    const totalWithTax = subtotal + orderTaxAmount;
    const totalWithShipping = totalWithTax + shippingCharges;
    const totalWithExpenses = totalWithShipping + additionalExpensesTotal;

    let roundingMultiple = 0;
    try {
      const savedPosSettings = localStorage.getItem('etpos_pos_settings');
      if (savedPosSettings) {
        const parsed = JSON.parse(savedPosSettings);
        if (parsed && parsed.amount_rounding_method) {
          roundingMultiple = parseFloat(parsed.amount_rounding_method) || 0;
        }
      }
    } catch (e) {}

    let autoRoundOff = 0;
    let finalTotal = totalWithExpenses;

    if (roundingMultiple > 0) {
      const rounded = Math.round(totalWithExpenses / roundingMultiple) * roundingMultiple;
      autoRoundOff = rounded - totalWithExpenses;
      finalTotal = rounded;
    }

    const calculationSteps = {
      productAmount: productAmount.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      subtotal: subtotal.toFixed(2),
      orderTax: orderTaxAmount.toFixed(2),
      shippingCharges: shippingCharges.toFixed(2),
      additionalExpenses: additionalExpensesTotal.toFixed(2),
      totalBeforeRoundOff: totalWithExpenses.toFixed(2),
      roundOffAmount: autoRoundOff.toFixed(2),
      finalTotal: finalTotal.toFixed(2)
    };

    setFormData(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      totalPayable: finalTotal.toFixed(2),
      roundOff: autoRoundOff.toFixed(2),
      calculationSteps: calculationSteps
    }));
  };

  const generateInvoiceHash = (saleId, invoiceNo) => {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2, 15);
    const combined = `${saleId}-${invoiceNo}-${timestamp}-${random}`;

    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
    const fullHash = (hexHash + random + timestamp).substring(0, 32).padEnd(32, '0');

    return fullHash;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!isEditMode) {
      const quotaOk = isQuotaAvailable('invoice_count');
      if (!quotaOk) {
        setShowQuotaModal(true);
        return;
      }
    }

    if (!formData.products || formData.products.length === 0) {
      setSuccessMessage('No product added. Add some product');
      return;
    }

    if (!validateForm()) {
      setSuccessMessage('');
      return;
    }

    let paymentsToUse = formData.payments || [];
    if (paymentsToUse.length === 0 && parseFloat(formData.amount || 0) > 0) {
      let accountName = 'None';
      if (formData.paymentAccount) {
        const selectedAccount = paymentAccounts.find(acc => acc.id == formData.paymentAccount || acc.name == formData.paymentAccount);
        if (selectedAccount) {
          accountName = selectedAccount.name || selectedAccount.account_name || selectedAccount.display_name;
        }
      }
      paymentsToUse = [{
        id: Date.now(),
        amount: formData.amount,
        paidOn: formData.paidOn ? String(formData.paidOn).replace('T', ' ') : dateUtils.nowUTC(),
        paymentMethod: formData.paymentMethod || 'Cash',
        paymentAccount: accountName,
        account_id: formData.paymentAccount,
        paymentNote: formData.paymentNote,
        cardNumber: formData.cardNumber,
        cardHolderName: formData.cardHolderName,
        cardType: formData.cardType,
        cardMonth: formData.cardMonth,
        cardYear: formData.cardYear,
        cardSecurity: formData.cardSecurity,
        cardTransactionNumber: formData.cardTransactionNumber,
        checkNo: formData.checkNo,
        checkExpiryDate: formData.checkExpiryDate,
        bankSubmissionDate: formData.bankSubmissionDate,
        bankAccountNumber: formData.bankAccountNumber
      }];
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      const sellData = {
        contact_id: parseInt(formData.customerId) || null,
        location_id: parseInt(formData.locationId),
        invoice_no: formData.invoiceNo || null,
        status: (formData.status === 'Final' || formData.status === 'final') ? 'final' : 'draft',
        is_direct_sale: 1,
        sub_status: formData.status === 'Final' ? null :
          formData.status === 'Draft' ? 'draft' :
            formData.status === 'Proforma' ? 'proforma' : 'quotation',
        is_quotation: formData.status === 'Quotation' ? 1 : 0,
        final_total: parseFloat(formData.totalPayable || 0),
        sale_date: dateUtils.formatUTCForApi(formData.saleDate || new Date()),
        transaction_date: dateUtils.formatUTCForApi(formData.saleDate || new Date()),
        tax_rate_id: (() => {
          const selectedTax = taxRates.find(t => t.name === formData.orderTax);
          return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
        })(),
        tax_amount: parseFloat(formData.orderTaxAmount || 0),
        discount_type: formData.discountType === 'Percentage' ? 'percentage' : 'fixed',
        discount_amount: parseFloat(formData.discountAmount || 0),
        is_credit_sale: paymentsToUse.length === 0,
        is_suspend: false,
        sale_note: formData.sellNote || null,
        additional_notes: formData.additionalNotes || null,
        staff_note: formData.staffNote || null,
        commission_agent: formData.commissionAgent ? parseInt(formData.commissionAgent) : null,
        exchange_rate: 1,
        price_group: formData.priceGroup ? parseInt(formData.priceGroup) : null,
        types_of_service_id: formData.typesOfServiceId ? parseInt(formData.typesOfServiceId) : null,
        invoice_scheme_id: formData.invoiceScheme ? parseInt(formData.invoiceScheme) : null,
        res_table_id: formData.resTableId ? parseInt(formData.resTableId) : null,
        res_waiter_id: formData.resWaiterId ? parseInt(formData.resWaiterId) : null,
        shipping_status: formData.shippingStatus && formData.shippingStatus !== 'Please Select' ? formData.shippingStatus : 'ordered',
        is_recurring: false,
        recurring_interval: null,
        recurring_interval_count: null,
        subscription_repeat_after: null,
        shipping_details: formData.shippingDetails || null,
        shipping_address: formData.shippingAddress || null,
        billing_address: formData.billingAddress || null,
        pay_term_number: formData.payTerm ? parseInt(formData.payTerm) : null,
        pay_term_type: formData.payTermPeriod ? formData.payTermPeriod.toLowerCase() : null,
        shipping_charges: parseFloat(formData.shippingCharges || 0),
        delivered_to: formData.deliveredTo || null,
        deliveredTo: formData.deliveredTo || null,
        delivery_person_id: formData.deliveryPerson ? parseInt(formData.deliveryPerson) : null,
        deliveryPerson: formData.deliveryPerson || null,

        service_custom_field_1: formData.serviceCustomField1 || null,
        service_custom_field_2: formData.serviceCustomField2 || null,
        service_custom_field_3: formData.serviceCustomField3 || null,
        service_custom_field_4: formData.serviceCustomField4 || null,
        service_custom_field_5: formData.serviceCustomField5 || null,
        service_custom_field_6: formData.serviceCustomField6 || null,

        additional_expense_key_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.name) || null,
        additional_expense_value_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.amount && formData.additionalExpenses[0].amount !== '') ? parseFloat(formData.additionalExpenses[0].amount) : 0,
        additional_expense_key_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.name) || null,
        additional_expense_value_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.amount && formData.additionalExpenses[1].amount !== '') ? parseFloat(formData.additionalExpenses[1].amount) : 0,
        additional_expense_key_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.name) || null,
        additional_expense_value_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.amount && formData.additionalExpenses[2].amount !== '') ? parseFloat(formData.additionalExpenses[2].amount) : 0,
        additional_expense_key_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.name) || null,
        additional_expense_value_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.amount && formData.additionalExpenses[3].amount !== '') ? parseFloat(formData.additionalExpenses[3].amount) : 0,
        round_off_amount: parseFloat(formData.roundOff || 0),

        products: formData.products.map(product => {
          let variationId = product.variation_id;
          if (!variationId || isNaN(parseInt(variationId))) {
            variationId = product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id;
          }
          variationId = parseInt(variationId);

          return {
            product_id: parseInt(product.id),
            variation_id: variationId,
            quantity: parseFloat(product.quantity),
            unit_price: parseFloat(product.unitPrice || product.sellPrice || 0),
            tax_id: (() => {
              const selectedTax = taxRates.find(t => t.name === product.tax);
              return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
            })(),
            discount_type: (product.discountType || '').toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
            discount_amount: parseFloat(product.discount || 0),
            line_order_status: product.lineOrderStatus || 'completed',
            lot_no: product.lotNo || null,
            expire_date: product.expireDate || null,
            woocommerce_line_item_id: null
          };
        }),

        payments: paymentsToUse && paymentsToUse.length > 0 ? paymentsToUse.map(payment => {
          let rawMethod = String(payment.paymentMethod || payment.method || payment.payment_method || 'cash').trim().toLowerCase();
          let method = 'cash';
          if (rawMethod.indexOf('card') !== -1) {
            method = 'card';
          } else if (rawMethod.indexOf('check') !== -1 || rawMethod.indexOf('cheque') !== -1) {
            method = 'cheque';
          } else if (rawMethod.indexOf('bank') !== -1 || rawMethod.indexOf('transfer') !== -1) {
            method = 'bank_transfer';
          } else if (rawMethod.indexOf('advance') !== -1) {
            method = 'advance';
          } else if (rawMethod.indexOf('cash') !== -1) {
            method = 'cash';
          } else {
            method = 'other';
          }
          const accId = parseInt(payment.account_id || payment.paymentAccount);
          return {
            amount: parseFloat(payment.amount),
            payment_amount: parseFloat(payment.amount),
            method: method,
            payment_method: method,
            note: payment.paymentNote || null,
            payment_note: payment.paymentNote || null,
            account_id: (!isNaN(accId) && accId > 0) ? accId : null,
            check_number: payment.checkNo || payment.checkNumber || null,
            cheque_number: payment.checkNo || payment.checkNumber || null,
            cheque_expiry_date: payment.checkExpiryDate || null,
            bank_submission_date: payment.bankSubmissionDate || null,
            card_number: payment.cardNumber || null,
            card_holder_name: payment.cardHolderName || null,
            card_type: payment.cardType || 'visa',
            card_month: payment.cardMonth || null,
            card_year: payment.cardYear || null,
            card_security: payment.cardSecurity || null,
            card_transaction_number: payment.cardTransactionNumber || null,
            bank_account_number: payment.bankAccountNumber || null,
            paid_on: dateUtils.formatDateForApi(payment.paidOn || formData.saleDate || new Date())
          };
        }) : []
      };

      let apiResponse;
      if (isEditMode && editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        const updateData = {
          ...sellData,
          sell_lines: sellData.products.map(p => ({
            product_id: p.product_id,
            variation_id: p.variation_id,
            quantity: p.quantity,
            unit_price: p.unit_price,
            line_discount_type: p.discount_type,
            line_discount_amount: p.discount_amount
          })),
          payments: sellData.payments
        };

        apiResponse = await apiService.updateSell(saleId, updateData);
        toast.success('Sale updated successfully!');
        setSuccessMessage('Sale updated successfully!');
      } else {
        apiResponse = await apiService.saveSell(sellData);
        toast.success('Sale saved successfully!');
        setSuccessMessage('Sale saved successfully!');
      }

      const finalSaleId = apiResponse.id || apiResponse.data?.id || apiResponse.transaction_id || apiResponse.data?.transaction_id;

      if (finalSaleId && formData.shippingStatus && formData.shippingStatus !== 'Please Select') {
        try {
          await apiService.updateShippingStatus(finalSaleId, formData.shippingStatus);
        } catch (shippingError) {
      /* console.error('Error updating shipping status:', shippingError); */
        }
      }

      const selectedCustomer = customers.find(c => c.id === parseInt(formData.customerId));

      const saleId = apiResponse.data?.id ||
        apiResponse.id ||
        apiResponse.data?.transaction_id ||
        apiResponse.transaction_id ||
        apiResponse.data?.sale_id ||
        apiResponse.sale_id;

      const saleInvoiceNo = apiResponse.data?.invoice_no ||
        apiResponse.invoice_no ||
        `INV-${Date.now()}`;

      let actualInvoiceUrl = null;
      let actualInvoiceNo = saleInvoiceNo;

      try {
        const salesList = await apiService.getSales({ per_page: 1, start_date: formData.saleDate, end_date: formData.saleDate });
        let ourSale = null;
        if (Array.isArray(salesList)) {
          ourSale = salesList.find(s => s.id === saleId || s.transaction_id === saleId);
        } else if (salesList.data && Array.isArray(salesList.data)) {
          ourSale = salesList.data.find(s => s.id === saleId || s.transaction_id === saleId);
        }

        if (ourSale) {
          const possibleUrlFields = ['invoice_url', 'invoiceLink', 'invoice_link', 'url', 'link'];
          for (const field of possibleUrlFields) {
            if (ourSale[field]) {
              actualInvoiceUrl = ourSale[field];
              break;
            }
          }
        }
      } catch (error) {
        const invoiceHash = generateInvoiceHash(saleId, saleInvoiceNo);
        actualInvoiceUrl = `https://et-pos.store/invoice/${invoiceHash}`;
      }

      if (!actualInvoiceUrl) {
        const invoiceHash = generateInvoiceHash(saleId, actualInvoiceNo);
        actualInvoiceUrl = `https://et-pos.store/invoice/${invoiceHash}`;
      }

      const saleDataForNotification = {
        id: saleId,
        invoiceNumber: actualInvoiceNo,
        date: formData.saleDate,
        total: formData.totalPayable,
        paymentStatus: apiResponse.data?.payment_status || apiResponse.payment_status || 'Due',
        invoiceUrl: actualInvoiceUrl,
        businessName: "Lefty's Liquor",
        items: formData.products.length
      };

      setLastSaleData(saleDataForNotification);

      setTimeout(() => {
        setShowWhatsAppModal(true);
      }, 400);

    } catch (error) {
      /* console.error('Error saving sale:', error); */
      setSuccessMessage(error.message || 'Error saving sale. Please try again.');
      toast.error(error.message || 'Error saving sale');
    }

    setIsSubmitting(false);
  };

  const handleSaveAndPrint = async (e) => {
    if (e) e.preventDefault();

    if (!isEditMode) {
      const quotaOk = isQuotaAvailable('invoice_count');
      if (!quotaOk) {
        setShowQuotaModal(true);
        return;
      }
    }

    if (!formData.products || formData.products.length === 0) {
      setSuccessMessage('No product added. Add some product');
      return;
    }

    if (!validateForm()) {
      setSuccessMessage('');
      return;
    }

    let paymentsToUse = formData.payments || [];

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      const sellData = {
        contact_id: parseInt(formData.customerId) || null,
        location_id: parseInt(formData.locationId) || null,
        invoice_no: formData.invoiceNo || null,
        status: (formData.status === 'Final' || formData.status === 'final') ? 'final' : 'draft',
        sub_status: formData.status === 'Final' ? null :
          formData.status === 'Draft' ? 'draft' :
            formData.status === 'Proforma' ? 'proforma' : 'quotation',
        is_quotation: formData.status === 'Quotation' ? 1 : 0,
        final_total: parseFloat(formData.totalPayable || 0),
        sale_date: dateUtils.formatDateForApi(formData.saleDate || new Date()),
        tax_rate_id: (() => {
          const selectedTax = taxRates.find(t => t.name === formData.orderTax);
          return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
        })(),
        tax_amount: parseFloat(formData.orderTaxAmount || 0),
        discount_type: formData.discountType === 'Percentage' ? 'percentage' : 'fixed',
        discount_amount: parseFloat(formData.discountAmount || 0),
        is_credit_sale: (formData.payments || []).length === 0,
        is_suspend: false,
        sale_note: formData.sellNote || null,
        additional_notes: formData.additionalNotes || null,
        staff_note: formData.staffNote || null,
        commission_agent: formData.commissionAgent ? parseInt(formData.commissionAgent) : null,
        exchange_rate: 1,
        price_group: formData.priceGroup ? parseInt(formData.priceGroup) : null,
        types_of_service_id: formData.typesOfServiceId ? parseInt(formData.typesOfServiceId) : null,
        invoice_scheme_id: formData.invoiceScheme ? parseInt(formData.invoiceScheme) : null,
        res_table_id: formData.resTableId ? parseInt(formData.resTableId) : null,
        res_waiter_id: formData.resWaiterId ? parseInt(formData.resWaiterId) : null,
        shipping_status: formData.shippingStatus && formData.shippingStatus !== 'Please Select' ? formData.shippingStatus : 'ordered',
        is_recurring: false,
        recurring_interval: null,
        recurring_interval_count: null,
        subscription_repeat_after: null,
        shipping_details: formData.shippingDetails || null,
        shipping_charges: parseFloat(formData.shippingCharges || 0),
        deliveredTo: formData.deliveredTo || null,
        deliveryPerson: formData.deliveryPerson || null,

        service_custom_field_1: formData.serviceCustomField1 || null,
        service_custom_field_2: formData.serviceCustomField2 || null,
        service_custom_field_3: formData.serviceCustomField3 || null,
        service_custom_field_4: formData.serviceCustomField4 || null,
        service_custom_field_5: formData.serviceCustomField5 || null,
        service_custom_field_6: formData.serviceCustomField6 || null,

        additional_expense_key_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.name) || null,
        additional_expense_value_1: (formData.additionalExpenses && formData.additionalExpenses[0]?.amount && formData.additionalExpenses[0].amount !== '') ? parseFloat(formData.additionalExpenses[0].amount) : 0,
        additional_expense_key_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.name) || null,
        additional_expense_value_2: (formData.additionalExpenses && formData.additionalExpenses[1]?.amount && formData.additionalExpenses[1].amount !== '') ? parseFloat(formData.additionalExpenses[1].amount) : 0,
        additional_expense_key_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.name) || null,
        additional_expense_value_3: (formData.additionalExpenses && formData.additionalExpenses[2]?.amount && formData.additionalExpenses[2].amount !== '') ? parseFloat(formData.additionalExpenses[2].amount) : 0,
        additional_expense_key_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.name) || null,
        additional_expense_value_4: (formData.additionalExpenses && formData.additionalExpenses[3]?.amount && formData.additionalExpenses[3].amount !== '') ? parseFloat(formData.additionalExpenses[3].amount) : 0,
        round_off_amount: parseFloat(formData.roundOff || 0),

        products: formData.products.map(product => {
          let variationId = product.variation_id;
          if (!variationId || isNaN(parseInt(variationId))) {
            variationId = product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id;
          }
          variationId = parseInt(variationId);

          return {
            product_id: parseInt(product.id),
            variation_id: variationId,
            quantity: parseFloat(product.quantity),
            unit_price: parseFloat(product.unitPrice || product.sellPrice || 0),
            tax_id: (() => {
              const selectedTax = taxRates.find(t => t.name === product.tax);
              return (selectedTax && selectedTax.id > 0) ? selectedTax.id : null;
            })(),
            discount_type: (product.discountType || '').toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
            discount_amount: parseFloat(product.discount || 0),
            line_order_status: product.lineOrderStatus || 'completed',
            lot_no: product.lotNo || null,
            expire_date: product.expireDate || null,
            woocommerce_line_item_id: null
          };
        }),

        payments: paymentsToUse && paymentsToUse.length > 0 ? paymentsToUse.map(payment => {
          let rawMethod = String(payment.paymentMethod || payment.method || payment.payment_method || 'cash').trim().toLowerCase();
          let method = 'cash';
          if (rawMethod.indexOf('card') !== -1) {
            method = 'card';
          } else if (rawMethod.indexOf('check') !== -1 || rawMethod.indexOf('cheque') !== -1) {
            method = 'cheque';
          } else if (rawMethod.indexOf('bank') !== -1 || rawMethod.indexOf('transfer') !== -1) {
            method = 'bank_transfer';
          } else if (rawMethod.indexOf('advance') !== -1) {
            method = 'advance';
          } else if (rawMethod.indexOf('cash') !== -1) {
            method = 'cash';
          } else {
            method = 'other';
          }
          return {
            payment_amount: parseFloat(payment.amount),
            payment_method: method,
            payment_note: payment.paymentNote || null,
          account_id: payment.account_id ? parseInt(payment.account_id) : null,
          check_number: payment.checkNo || payment.checkNumber || null,
          cheque_number: payment.checkNo || payment.checkNumber || null,
          cheque_expiry_date: payment.checkExpiryDate || null,
          bank_submission_date: payment.bankSubmissionDate || null,
          card_number: payment.cardNumber || null,
          card_holder_name: payment.cardHolderName || null,
          card_type: payment.cardType || 'visa',
          card_month: payment.cardMonth || null,
          card_year: payment.cardYear || null,
          card_security: payment.cardSecurity || null,
          card_transaction_number: payment.cardTransactionNumber || null,
          bank_account_number: payment.bankAccountNumber || null,
          paid_on: dateUtils.formatDateForApi(payment.paidOn || formData.saleDate || new Date())
        };
      }) : []
      };

      let apiResponse;
      let finalSaleId;

      if (isEditMode && editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        apiResponse = await apiService.updateSell(saleId, sellData);
        toast.success('Sale updated successfully!');
        finalSaleId = saleId;
      } else {
        apiResponse = await apiService.saveSell(sellData);
        finalSaleId = apiResponse.id || apiResponse.data?.id || apiResponse.transaction_id || apiResponse.data?.transaction_id;
      }

      if (finalSaleId && formData.shippingStatus && formData.shippingStatus !== 'Please Select') {
        try {
          await apiService.updateShippingStatus(finalSaleId, formData.shippingStatus);
        } catch (shippingError) {
      /* console.error('Error updating shipping status for print:', shippingError); */
        }
      }

      setTimeout(() => {
        const invoiceUrl = apiResponse.invoice_url || apiResponse.data?.invoice_url;
        if (invoiceUrl) {
          window.open(invoiceUrl, '_blank');
        } else {
          toast.error('Invoice URL not available for print.');
        }
        setSuccessMessage('');
      }, 500);

    } catch (error) {
      /* console.error('Error saving sale for print:', error); */
      setSuccessMessage(`Error saving sale: ${error.message || 'Please try again.'}`);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="sell-form-container add-sell-override">
      {/* Header */}
      <div className="page-header">
        <button
          onClick={() => navigate('/sell')}
          className="back-button"
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px'
          }}
          title="Back to Sell Lists"
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem' }}>{isEditMode ? (isSalesOrder ? 'Edit Sales Order' : 'Edit Sale') : (isSalesOrder ? 'Sales Order' : 'Add Sale')}</h2>
      </div>

      <form onSubmit={handleSubmit} className="sell-form">
        {successMessage && (
          <div className={`message ${successMessage.includes('Error') ? 'error-message' : 'success-message'}`}>
            {successMessage}
          </div>
        )}

        {/* Customer Information Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer">Customer:*</label>
              <div className="customer-dropdown-container">
                <div className="input-with-icon-and-button">
                  <div className="input-icon-box">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    id="customer"
                    name="customer"
                    value={customerSearchTerm}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    onFocus={handleCustomerInputFocus}
                    onBlur={handleCustomerInputBlur}
                    className={`form-input-clean ${errors.customer ? 'error' : ''}`}
                    placeholder="Search customer..."
                    autoComplete="off"
                  />
                  <button 
                    type="button" 
                    className="add-customer-quick-btn" 
                    onClick={() => setShowAddCustomerModal(true)}
                    title="Add New Customer"
                  >
                    <PlusCircle size={22} color="white" fill="#3b82f6" style={{ borderRadius: '50%' }} />
                  </button>
                </div>
                {showCustomerDropdown && (
                  <div className="customer-dropdown">
                    {isLoadingCustomers ? (
                      <div className="customer-dropdown-item loading">Loading...</div>
                    ) : customerError ? (
                      <div className="customer-dropdown-item error">{customerError}</div>
                    ) : filteredCustomers.length > 0 ? (
                      filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-dropdown-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCustomerSelect(customer);
                          }}
                        >
                          <div className="customer-name">{customer.name}</div>
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item no-results">No results</div>
                    )}
                  </div>
                )}
              </div>
              {errors.customer && <span className="error-text">{errors.customer}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="billingAddress">Billing Address:</label>
              <input
                type="text"
                id="billingAddress"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingAddress">Shipping Address:</label>
              <input
                type="text"
                id="shippingAddress"
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="payTerm">Pay term:{isPayTermRequired && <span style={{ color: '#ef4444' }}>*</span>}</label>
              <div className="payterm-input-group">
                <input
                  type="number"
                  id="payTerm"
                  name="payTerm"
                  value={formData.payTerm}
                  onChange={handleInputChange}
                  className={`form-input payterm-input ${errors.payTerm ? 'is-invalid' : ''}`}
                  min="0"
                />
                <select
                  id="payTermPeriod"
                  name="payTermPeriod"
                  value={formData.payTermPeriod || 'Months'}
                  onChange={handleInputChange}
                  className="form-select payterm-select"
                >
                  <option value="Months">Mon</option>
                  <option value="Days">Day</option>
                  <option value="Years">Yr</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            {salesCommissionAgent && salesCommissionAgent !== 'disable' && salesCommissionAgent !== 'logged_in_user' && (
              <div className="form-group">
                <label htmlFor="commissionAgent">Comm. Agent:{isCommissionAgentRequired && <span style={{ color: '#ef4444' }}>*</span>}</label>
                <select
                  id="commissionAgent"
                  name="commissionAgent"
                  value={formData.commissionAgent}
                  onChange={handleInputChange}
                  className={`form-select ${errors.commissionAgent ? 'is-invalid' : ''}`}
                >
                  <option value="">Select</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(enabledModules.includes('service_staff') || enabledModules.includes('tables')) && (
              <div className="form-group">
                <label htmlFor="serviceStaff">Service staff</label>
                <select
                  id="serviceStaff"
                  name="serviceStaff"
                  value={formData.serviceStaff}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {serviceStaffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="saleDate">Sale Date:*</label>
              <input
                type="date"
                id="saleDate"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleInputChange}
                className={`form-input ${errors.saleDate ? 'error' : ''}`}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status:*</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={`form-select ${errors.status ? 'error' : ''}`}
              >
                <option value="">Select</option>
                <option value="Final">Final</option>
                <option value="Draft">Draft</option>
                <option value="Quotation">Quote</option>
                <option value="Proforma">Proforma</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="locationId">Business Location:*</label>
              <select
                id="locationId"
                name="locationId"
                value={formData.locationId}
                onChange={handleInputChange}
                className={`form-select ${errors.locationId ? 'error' : ''}`}
              >
                {businessLocations.length === 0 ? (
                  <option value="">Loading...</option>
                ) : (
                  <>
                    <option value="">Select Location</option>
                    {businessLocations.map(loc => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name || loc.location_name || `Location ${loc.id}`}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {errors.locationId && <span className="error-text">{errors.locationId}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="invoiceScheme">Invoice scheme:</label>
              <select
                id="invoiceScheme"
                name="invoiceScheme"
                value={formData.invoiceScheme}
                onChange={handleInputChange}
                className="form-select"
              >
                {isLoadingInvoiceSchemes ? (
                  <option value="">Loading...</option>
                ) : (
                  <>
                    <option value="">Select</option>
                    {invoiceSchemes.map(scheme => (
                      <option key={scheme.id} value={scheme.id}>
                        {scheme.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="invoiceNo">{isSalesOrder ? 'Order No.:' : 'Invoice No.:'}</label>
              <input
                type="text"
                id="invoiceNo"
                name="invoiceNo"
                value={formData.invoiceNo}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Auto"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveredTo">Delivered To:</label>
              <input
                type="text"
                id="deliveredTo"
                name="deliveredTo"
                value={formData.deliveredTo}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryPerson">Delivery Person:</label>
              <select
                id="deliveryPerson"
                name="deliveryPerson"
                value={formData.deliveryPerson}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Please Select</option>
                {usersList.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Product Section */}
        <div className="form-section-content">
          <h3>Product Information</h3>
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="productName">Enter Product name / SKU / Scan bar code</label>
              <div className="product-search-wrapper" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="product-dropdown-container" style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    id="productName"
                    name="productName"
                    value={productSearchTerm}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={handleProductInputFocus}
                    onBlur={handleProductInputBlur}
                    className="form-input"
                    placeholder="Search products by name or SKU..."
                    autoComplete="off"
                    style={{ paddingRight: '10px' }}
                  />

                {isLoadingProducts && (
                  <div className="product-loading">Loading products...</div>
                )}
                {showProductDropdown && !isLoadingProducts && (
                  <div className="product-dropdown">
                    {productError ? (
                      <div className="product-dropdown-item error">
                        {productError}
                      </div>
                    ) : filteredProducts.length > 0 ? (
                      <>
                        {filteredProducts.slice(0, displayLimit).map((product) => (
                          <div
                            key={product.id}
                            className="product-dropdown-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleProductSelect(product);
                            }}
                          >
                            <div className="product-name">
                              {product.product || product.name || product.product_name || product.title || product.description || 'Unknown Product'}
                              {(product.sku || product.code) && <span className="product-sku-inline"> • SKU: {product.sku || product.code}</span>}
                            </div>
                            <div className="product-details">
                              {(product.unit_price || product.price || product.cost) && <span className="product-price">${product.unit_price || product.price || product.cost}</span>}
                              {product.barcode && <span className="product-barcode">Barcode: {product.barcode}</span>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > displayLimit && (
                          <div
                            className="product-dropdown-item show-more"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDisplayLimit(prev => prev + 50);
                            }}
                          >
                            Show {Math.min(50, filteredProducts.length - displayLimit)} more products...
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="product-dropdown-item no-results">
                        No products found
                      </div>
                    )}
                  </div>
                )}
              </div>
                <button
                  type="button"
                  className="add-product-btn-icon"
                  onClick={() => setShowAddProductModal(true)}
                  style={{
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                  title="Add New Product"
                >
                  <PlusCircle size={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="product-table" style={{ overflowX: 'auto', margin: '1rem 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '28%' }}>Product</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '22%' }}>Quantity</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '11%' }}>Unit Price</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '13%' }}>Discount</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '10%' }}>Tax</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '8%' }}>Price inc. tax</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '8%' }}>Subtotal</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', width: '6%' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.products.length > 0 ? (
                  formData.products.map((product) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div className="product-info-cell">
                          <div className="product-name" style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.875rem' }}>{product.name}</div>
                          {product.sku && <div className="product-sku" style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '2px' }}>{product.sku}</div>}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden', background: '#fff' }}>
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(product.id, Math.max(1, (parseInt(product.quantity) || 0) - 1))}
                              style={{ background: '#fff', color: '#ef4444', border: 'none', borderRight: '1px solid #e2e8f0', width: '26px', height: '30px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={product.quantity}
                              onChange={(e) => updateProductQuantity(product.id, e.target.value)}
                              style={{ width: '38px', height: '30px', textAlign: 'center', border: 'none', outline: 'none', fontSize: '0.875rem', fontWeight: '600', color: '#1e293b' }}
                              min="1"
                            />
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(product.id, (parseInt(product.quantity) || 0) + 1)}
                              style={{ background: '#fff', color: '#22c55e', border: 'none', borderLeft: '1px solid #e2e8f0', width: '26px', height: '30px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              +
                            </button>
                          </div>
                          <select className="unit-select" style={{ padding: '4px 8px', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', height: '32px' }}>
                            <option value="Single">Single</option>
                          </select>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <input
                          type="number"
                          className="form-input unit-price-input"
                          value={product.unitPrice}
                          readOnly
                          style={{ width: '100%', maxWidth: '80px', padding: '5px 8px', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="number"
                            value={product.discount}
                            onChange={(e) => updateProductDiscount(product.id, e.target.value)}
                            style={{ width: '55px', padding: '5px 6px', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #cbd5e1', textAlign: 'center' }}
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                          <select
                            value={product.discountType === 'Percentage' ? '%' : '$'}
                            onChange={(e) => updateProductDiscountType(product.id, e.target.value === '%' ? 'Percentage' : 'Fixed')}
                            style={{ padding: '5px 4px', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', height: '32px' }}
                          >
                            <option value="%">%</option>
                            <option value="$">$</option>
                          </select>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <select
                          value={product.tax}
                          onChange={(e) => updateProductTax(product.id, e.target.value)}
                          style={{ width: '100%', minWidth: '75px', padding: '5px 6px', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', height: '32px' }}
                        >
                          {taxRates.map((tax) => (
                            <option key={tax.id} value={tax.name}>
                              {tax.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: '700', color: '#1e293b' }}>
                        ${(parseFloat(product.priceIncTax) || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: '700', color: '#1e293b' }}>
                        ${(parseFloat(product.subtotal) || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            type="button"
                            className="action-btn view-btn"
                            onClick={() => setViewProduct(product)}
                            style={{
                              background: '#06b6d4',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            title="View Product Details"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            className="action-btn remove-btn"
                            onClick={() => removeProduct(product.id)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            title="Remove Product"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>No products added</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="product-summary">
            <span>Items: {formData.products.length}</span>
            <span>Total: ${formData.products.reduce((sum, product) => sum + (parseFloat(product.subtotal) || 0), 0).toFixed(2)}</span>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Shipping Section */}
        <div className="form-section-content">
          <h3>Shipping Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shippingDetails">Shipping Details</label>
              <textarea
                id="shippingDetails"
                name="shippingDetails"
                value={formData.shippingDetails}
                onChange={handleInputChange}
                className="form-textarea"
                rows="1"
                style={{ minHeight: '64px' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingCharges">Shipping Charges</label>
              <input
                type="number"
                id="shippingCharges"
                name="shippingCharges"
                value={formData.shippingCharges}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="shippingStatus">Shipping Status</label>
              <select
                id="shippingStatus"
                name="shippingStatus"
                value={formData.shippingStatus}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Please Select</option>
                <option value="ordered">Ordered</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group shipping-document-group">
              <label>Shipping Documents:</label>
              <div className="shipping-doc-input-wrapper">
                <input
                  type="file"
                  className="form-input"
                  accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                />
                <span className="file-hint">Max 5MB (PDF, JPG, PNG...)</span>
              </div>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Additional Expenses & Round Off Section */}
        <div className="form-section-content">
          <h3
            className="calculation-header"
            onClick={() => setIsExpensesExpanded(!isExpensesExpanded)}
            style={{ cursor: 'pointer', userSelect: 'none', marginBottom: isExpensesExpanded ? '0.75rem' : '0' }}
          >
            Additional Expenses & Round Off
            <span style={{ marginLeft: '8px', fontSize: '0.8em' }}>
              {isExpensesExpanded ? '▼' : '▶'}
            </span>
          </h3>

          {isExpensesExpanded && (
            <>
              <div className="expenses-table">
                <table>
                  <thead>
                    <tr>
                      <th>Expense Name</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.additionalExpenses.map((expense, index) => (
                      <tr key={expense.id}>
                        <td>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Expense name"
                            value={expense.name}
                            onChange={(e) => handleExpenseChange(index, 'name', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="0.00"
                            value={expense.amount}
                            onChange={(e) => handleExpenseChange(index, 'amount', e.target.value)}
                            step="0.01"
                            min="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-row" style={{ marginTop: '0.5rem' }}>
                <div className="form-group">
                  <label>Auto Round Off:</label>
                  <div className="calculation-steps" style={{ padding: '0.25rem 0.5rem', background: '#f8fafc' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: parseFloat(formData.roundOff) !== 0 ? 'var(--etpos-warning)' : 'inherit' }}>
                      {parseFloat(formData.roundOff) >= 0 ? '+' : ''}{formData.roundOff}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <hr className="section-divider" />

        {/* Add Payment Section */}
        <div className="form-section-content">
          <h3>Add payment</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="amount">Amount:*</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="paidOn">Paid on:*</label>
              <input
                type="text"
                id="paidOn"
                name="paidOn"
                value={formData.paidOn}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method:*</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="form-select"
              >
                {!formData.locationId ? (
                  <option value="">Select Business Location First</option>
                ) : paymentMethods.length === 0 ? (
                  <option value="">No payment methods available</option>
                ) : (
                  paymentMethods.map(method => (
                    <option key={method.id} value={method.name}>
                      {method.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="paymentAccount">Payment Account:</label>
              <select
                id="paymentAccount"
                name="paymentAccount"
                value={formData.paymentAccount}
                onChange={handleInputChange}
                className="form-select"
              >
                {!formData.locationId ? (
                  <option value="">Select Business Location First</option>
                ) : (
                  <>
                    <option value="None">None</option>
                    {paymentAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name || account.account_name || account.display_name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Conditional Card Payment Fields matching production screenshot */}
          {(formData.paymentMethod === 'Card' || formData.paymentMethod === 'card') && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cardNumber">Card Number:</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cardHolderName">Card Holder Name:</label>
                <input
                  type="text"
                  id="cardHolderName"
                  name="cardHolderName"
                  value={formData.cardHolderName}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cardExpiry">Card Expiry:</label>
                <input
                  type="text"
                  id="cardExpiry"
                  name="cardExpiry"
                  value={formData.cardExpiry}
                  onChange={handleInputChange}
                  placeholder="MM/YY"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cardCvv">CVV:</label>
                <input
                  type="text"
                  id="cardCvv"
                  name="cardCvv"
                  value={formData.cardCvv}
                  onChange={handleInputChange}
                  placeholder="123"
                  className="form-input"
                />
              </div>
            </div>
          )}

          {/* Conditional Check / Cheque Payment Fields matching production screenshot */}
          {(formData.paymentMethod === 'Cheque' || formData.paymentMethod === 'Check' || formData.paymentMethod === 'cheque') && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="checkNo">Check No.:</label>
                <input
                  type="text"
                  id="checkNo"
                  name="checkNo"
                  value={formData.checkNo}
                  onChange={handleInputChange}
                  placeholder="Check No."
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="checkExpiryDate">Check Expiry Date:</label>
                <input
                  type="date"
                  id="checkExpiryDate"
                  name="checkExpiryDate"
                  value={formData.checkExpiryDate}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="bankSubmissionDate">Bank Submission Date:</label>
                <input
                  type="date"
                  id="bankSubmissionDate"
                  name="bankSubmissionDate"
                  value={formData.bankSubmissionDate}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="checkImage">Check Images:</label>
                <input
                  type="file"
                  id="checkImage"
                  name="checkImage"
                  onChange={(e) => setFormData(prev => ({ ...prev, checkImage: e.target.files[0] }))}
                  accept="image/jpeg, image/png, application/pdf"
                  className="form-input"
                  style={{ padding: '0.3rem' }}
                />
              </div>
            </div>
          )}

          {/* Conditional Bank Transfer Payment Fields */}
          {(formData.paymentMethod === 'Bank Transfer' || formData.paymentMethod === 'bank_transfer') && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="bankAccountNumber">Bank Account Number:*</label>
                <input
                  type="text"
                  id="bankAccountNumber"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleInputChange}
                  placeholder="Enter bank account number"
                  className="form-input"
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="discountType">Discount Type:*</label>
              <select
                id="discountType"
                name="discountType"
                value={formData.discountType}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="discountAmount">Discount Amount:*</label>
              <input
                type="number"
                id="discountAmount"
                name="discountAmount"
                value={formData.discountAmount}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>(-): 0.00</div>
            </div>
            <div className="form-group">
              <label htmlFor="orderTax">Order Tax:*</label>
              <select
                id="orderTax"
                name="orderTax"
                value={formData.orderTax}
                onChange={handleInputChange}
                className="form-select"
              >
                {taxRates.map(tax => (
                  <option key={tax.id} value={tax.name}>
                    {tax.name}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>(+): 0.00</div>
            </div>
            <div className="form-group">
              <label htmlFor="sellNote">Sell note</label>
              <textarea
                id="sellNote"
                name="sellNote"
                value={formData.sellNote}
                onChange={handleInputChange}
                className="form-textarea"
                rows="1"
                style={{ minHeight: '38px' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="paymentNote">Payment note:</label>
              <textarea
                id="paymentNote"
                name="paymentNote"
                value={formData.paymentNote}
                onChange={handleInputChange}
                className="form-textarea"
                rows="2"
              />
            </div>
          </div>

          {/* Totals Summary Bar matching Screenshot 2 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'var(--etpos-bg, #f8fafc)',
            padding: '1.25rem 2rem',
            borderRadius: '8px',
            margin: '1.5rem 0 1rem 0',
            fontWeight: '600',
            fontSize: '1.05rem',
            color: 'var(--etpos-text, #1e293b)'
          }}>
            <div>Total Payable: ${formData.totalPayable}</div>
            <div>Change Return: ${changeReturn}</div>
            <div>Balance: ${balance}</div>
          </div>

          {/* Add Payment Button */}
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              type="button"
              onClick={handleAddPaymentEntry}
              style={{
                background: '#4f46e5',
                color: '#fff',
                border: 'none',
                padding: '0.65rem 1.4rem',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              + Add Payment
            </button>
          </div>

          {/* Payment Entries (Matching Production Screenshot 2) */}
          {formData.payments.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '1rem' }}>Payment Entries</h3>
              <div className="payments-list" style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', padding: '0.75rem 1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.5fr 1.5fr 120px', gap: '1rem', paddingBottom: '0.25rem', paddingTop: '0.25rem', color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.85rem', fontWeight: '600' }}>
                  <div>Amount</div>
                  <div>Paid on</div>
                  <div>Method</div>
                  <div>Account</div>
                  <div></div>
                </div>
                {formData.payments.map((payment) => (
                  <div key={payment.id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.5fr 1.5fr 120px', gap: '1rem', paddingTop: '0.25rem', paddingBottom: '0.25rem', alignItems: 'center' }}>
                    <div style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)', fontSize: '0.9rem' }}>${payment.amount}</div>
                    <div style={{ color: 'var(--etpos-text-muted, #334155)', fontSize: '0.875rem', fontWeight: '500' }}>{payment.paidOn ? String(payment.paidOn).replace('T', ' ') : ''}</div>
                    <div style={{ color: 'var(--etpos-text-muted, #334155)', fontSize: '0.875rem', fontWeight: '500' }}>{payment.paymentMethod}</div>
                    <div style={{ color: 'var(--etpos-text-muted, #334155)', fontSize: '0.875rem', fontWeight: '500' }}>{payment.paymentAccount || 'None'}</div>
                    <div style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleRemovePayment(payment.id)}
                        style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.35rem 1rem', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button
              type="button"
              className="save-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Saving...' : '💾 Save'}
            </button>
            <button
              type="button"
              className="save-print-btn"
              onClick={handleSaveAndPrint}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Processing...' : '🖨️ Save & Print'}
            </button>
          </div>
        </div>
      </form>

      {/* WhatsApp Notification Modal */}
      {showWhatsAppModal && lastSaleData && (
        <WhatsAppIntegration
          saleData={lastSaleData}
          customerData={customers.find(c => c.id === parseInt(formData.customerId)) || {}}
          onClose={() => {
            setShowWhatsAppModal(false);
            setLastSaleData(null);
            setFormData({
              ...initialFormData,
              saleDate: dateUtils.getChicagoISOString().split('T')[0],
              paidOn: dateUtils.formatDateTime(new Date()),
            });
            setErrors({});
            setSuccessMessage('');
            setCustomerSearchTerm('');
            navigate('/sell', { state: { reloadSales: Date.now() } });
          }}
        />
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowAddCustomerModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>
            <div className="modal-header" style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Add New Customer</h2>
              <button className="close-button" onClick={() => {
                setShowAddCustomerModal(false);
                loadCustomers();
              }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '20px' }}>
              <AddSupplier onSuccess={(newContact) => {
                setShowAddCustomerModal(false);
                loadCustomers().then(() => {
                  if (newContact) {
                    const mappedCustomer = {
                      id: newContact.id,
                      name: newContact.name || `${newContact.first_name || ''} ${newContact.last_name || ''}`.trim() || 'Unknown',
                      phone: newContact.mobile || newContact.contact_no || newContact.phone || '',
                      email: newContact.email || '',
                      address: newContact.address_line_1 || newContact.address || '',
                      balance: newContact.balance || '0.00',
                      advance_balance: newContact.advance_balance || '0.00',
                      due_amount: newContact.due_amount || '0.00',
                      pay_term_number: newContact.pay_term_number,
                      pay_term_type: newContact.pay_term_type
                    };
                    handleCustomerSelect(mappedCustomer);
                  }
                });
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="modal-overlay" onClick={() => setShowAddProductModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>
            <div className="modal-header" style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Add New Product</h2>
              <button className="close-button" onClick={() => setShowAddProductModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '20px' }}>
              <AddProduct onSuccess={() => {
                setShowAddProductModal(false);
                loadProducts();
              }} />
            </div>
          </div>
        </div>
      )}

      {/* View Product Details Modal (Matching Image 2) */}
      {viewProduct && (
        <div className="modal-overlay" onClick={() => setViewProduct(null)} style={{ zIndex: 9999, background: 'rgba(0, 0, 0, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '90%', borderRadius: '12px', padding: '0', background: 'var(--etpos-card, #fff)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: 'var(--etpos-text, #1e293b)', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                {viewProduct.name}
              </h3>
              <button
                type="button"
                onClick={() => setViewProduct(null)}
                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--etpos-text-muted, #64748b)', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '12px 24px 24px 24px', display: 'flex', flexDirection: 'column', gap: '0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Product Name</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)', textAlign: 'right', fontWeight: '600', maxWidth: '60%' }}>{viewProduct.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>SKU</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{viewProduct.sku || '--'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Unit Price</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>${(parseFloat(viewProduct.unitPrice) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Previous unit price</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>${(parseFloat(viewProduct.purchasePrice) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Price Inc. Tax</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>${(parseFloat(viewProduct.priceIncTax) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Quantity</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{viewProduct.quantity || 1}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Discount</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{viewProduct.discount || 0}{viewProduct.discountType === 'Percentage' ? '%' : '$'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Previous discount</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>$0.00</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Tax</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{viewProduct.tax || 'None'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--etpos-border, #f1f5f9)', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Subtotal</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>${(parseFloat(viewProduct.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>Stock Available</span>
                <span style={{ color: 'var(--etpos-text-muted, #475569)' }}>{parseFloat(viewProduct.stock_available || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Quota Exhausted Modal */}
      {showQuotaModal && (
        <SubscriptionExpiredModal
          isOpen={showQuotaModal}
          onClose={() => setShowQuotaModal(false)}
          title="Monthly Invoice Limit Reached"
          message="You have reached your monthly invoice creation limit under your current plan. Please upgrade your subscription package to continue creating sales."
        />
      )}
    </div>
  );
};

export default AddSell;
